import {
  isCivilDate,
  parseWeeklyCollection,
  parseWeeklyPeriod,
  parseWeeklyStatement,
} from "./org.utils";
import type {
  WeeklyStatement,
  WeeklyStatementLine,
  WeeklyPeriod,
  WeeklyCollection,
} from "./org.utils";

export type WeeklyStatementSummary = Omit<WeeklyStatement, "lines"> & { groupCount: number };

export type WeeklyEconomicGroup = {
  groupId: string;
  kind: string;
  amountCents: number;
  metric?: "view" | "access";
  occurrenceCount?: number;
  baseSourceCount?: number;
  serviceDates?: string[];
  dailyAmounts?: { serviceDate: string; amountCents: number; sourceCount?: number }[];
  quoteVersion?: string;
  economicCohortKey?: string;
  firstOccurredAt?: string;
  lastOccurredAt?: string;
  originInvoiceId?: string;
  originPeriod?: { startsOn: string; endsOn: string };
} & (
  | Omit<Extract<WeeklyStatementLine, { lineClass: "service" }>, "lineId">
  | Omit<
      Extract<
        WeeklyStatementLine,
        { lineClass: "customer_credit" | "processing_fee" | "negotiation_topup" }
      >,
      "lineId"
    >
);

export type WeeklyStatementPage = {
  invoiceId: string;
  invoiceStatus: string;
  weeklyPeriod?: WeeklyPeriod;
  weeklyCollection?: WeeklyCollection;
  weeklyBilling?: WeeklyBilling;
  summary: WeeklyStatementSummary;
  totalPages: number;
  groups: WeeklyEconomicGroup[];
};

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const text = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "" && value.length <= 200;
const integer = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value);
const timestamp = (value: unknown): value is string =>
  text(value) && /^\d{4}-\d{2}-\d{2}T/.test(value) && Number.isFinite(Date.parse(value));

/** Whole-invoice amounts are supplied by Payments, independently of the selected group page. */
export function parseWeeklyStatementSummary(
  value: unknown,
): WeeklyStatementSummary | undefined | null {
  if (value === undefined) return undefined;
  if (
    !record(value) ||
    !integer(value.serviceAmountCents) ||
    value.serviceAmountCents < 0 ||
    !integer(value.totalAmountCents) ||
    value.totalAmountCents < 0 ||
    !integer(value.groupCount) ||
    value.groupCount < 0
  )
    return null;
  const {
    customerCreditAmountCents: credit,
    processingFeeAmountCents: fee,
    negotiationTopupAmountCents: topup,
  } = value;
  if (credit !== undefined && (!integer(credit) || credit >= 0)) return null;
  if (fee !== undefined && (!integer(fee) || fee <= 0)) return null;
  // The Saldo de Negociações top-up line is a deposit, not a service: it is
  // part of the debit total and never of serviceAmountCents.
  if (topup !== undefined && (!integer(topup) || topup <= 0)) return null;
  const sum = value.serviceAmountCents + (credit ?? 0) + (fee ?? 0) + (topup ?? 0);
  if (!Number.isSafeInteger(sum) || sum !== value.totalAmountCents) return null;
  return {
    serviceAmountCents: value.serviceAmountCents,
    totalAmountCents: value.totalAmountCents,
    groupCount: value.groupCount,
    ...(credit !== undefined ? { customerCreditAmountCents: credit } : {}),
    ...(fee !== undefined ? { processingFeeAmountCents: fee } : {}),
    ...(topup !== undefined ? { negotiationTopupAmountCents: topup } : {}),
  };
}

export type WeeklyBilling = {
  policyVersion: 2;
  minimumNetServicesCents: number;
  state: string;
  holdReason?: string;
  disposition: "contribution" | "accumulation";
  ownServiceAmountCents: number;
  carriedServiceAmountCents: number;
  eligibleCustomerCreditAmountCents?: number;
  netServiceAmountCents?: number;
  targetInvoiceId?: string;
  expectedChargeOn?: string;
};

/** Policy and carry are server authority; the browser never chooses a target or reprices sources. */
export function parseWeeklyBilling(
  value: unknown,
  summary?: WeeklyStatementSummary,
): WeeklyBilling | undefined | null {
  if (value === undefined) return undefined;
  if (
    !record(value) ||
    value.policyVersion !== 2 ||
    value.minimumNetServicesCents !== 100 ||
    !text(value.state) ||
    !["contribution", "accumulation"].includes(String(value.disposition)) ||
    !integer(value.ownServiceAmountCents) ||
    value.ownServiceAmountCents < 0 ||
    !integer(value.carriedServiceAmountCents) ||
    value.carriedServiceAmountCents < 0
  )
    return null;
  const result: WeeklyBilling = {
    policyVersion: 2,
    minimumNetServicesCents: value.minimumNetServicesCents,
    state: value.state,
    disposition: value.disposition as WeeklyBilling["disposition"],
    ownServiceAmountCents: value.ownServiceAmountCents,
    carriedServiceAmountCents: value.carriedServiceAmountCents,
  };
  for (const field of ["holdReason", "targetInvoiceId"] as const) {
    if (value[field] !== undefined) {
      if (!text(value[field])) return null;
      result[field] = value[field];
    }
  }
  if (value.eligibleCustomerCreditAmountCents !== undefined) {
    if (
      !integer(value.eligibleCustomerCreditAmountCents) ||
      value.eligibleCustomerCreditAmountCents >= 0
    )
      return null;
    result.eligibleCustomerCreditAmountCents = value.eligibleCustomerCreditAmountCents;
  }
  if (value.netServiceAmountCents !== undefined) {
    if (!integer(value.netServiceAmountCents) || value.netServiceAmountCents < 0) return null;
    result.netServiceAmountCents = value.netServiceAmountCents;
  }
  if (result.disposition === "contribution") {
    if (
      !result.targetInvoiceId ||
      result.carriedServiceAmountCents !== 0 ||
      result.netServiceAmountCents !== undefined ||
      result.eligibleCustomerCreditAmountCents !== undefined ||
      value.expectedChargeOn !== undefined
    )
      return null;
  } else {
    if (result.targetInvoiceId || result.netServiceAmountCents === undefined) return null;
    const gross = result.ownServiceAmountCents + result.carriedServiceAmountCents;
    const credit =
      result.eligibleCustomerCreditAmountCents ?? summary?.customerCreditAmountCents ?? 0;
    if (
      !Number.isSafeInteger(gross) ||
      !Number.isSafeInteger(gross + credit) ||
      gross + credit !== result.netServiceAmountCents
    )
      return null;
    if (
      summary?.customerCreditAmountCents !== undefined &&
      result.eligibleCustomerCreditAmountCents !== undefined
    )
      return null;
    if (value.expectedChargeOn !== undefined) {
      if (
        !isCivilDate(value.expectedChargeOn) ||
        !["awaiting_preparation", "closed", "paid", "recovery", "failed"].includes(result.state)
      )
        return null;
      result.expectedChargeOn = value.expectedChargeOn;
    }
  }
  if (
    summary &&
    summary.serviceAmountCents !== result.ownServiceAmountCents + result.carriedServiceAmountCents
  )
    return null;
  return result;
}

/** Never infer a billed metric, quantity or unit price from kind or amount. */
export function parseWeeklyStatementPage(value: unknown): WeeklyStatementPage | null {
  if (
    !record(value) ||
    !text(value.invoiceId) ||
    !text(value.invoiceStatus) ||
    !integer(value.totalPages) ||
    value.totalPages < 0 ||
    !Array.isArray(value.groups) ||
    value.groups.length > 100
  )
    return null;
  const summary = parseWeeklyStatementSummary(value.summary);
  const weeklyPeriod = parseWeeklyPeriod(value.weeklyPeriod);
  const weeklyCollection = parseWeeklyCollection(value.weeklyCollection);
  const weeklyBilling = parseWeeklyBilling(value.weeklyBilling, summary ?? undefined);
  if (!summary || weeklyPeriod === null || weeklyCollection === null || weeklyBilling === null)
    return null;
  if (
    value.groups.length > summary.groupCount ||
    (summary.groupCount === 0 && (value.totalPages !== 0 || value.groups.length !== 0)) ||
    (summary.groupCount > 0 && value.totalPages === 0)
  )
    return null;
  const groups: WeeklyEconomicGroup[] = [];
  const ids = new Set<string>();
  for (const candidate of value.groups) {
    if (!record(candidate) || !text(candidate.groupId) || ids.has(candidate.groupId)) return null;
    ids.add(candidate.groupId);
    let line: WeeklyStatementLine;
    if (candidate.lineClass === "customer_credit") {
      if (
        candidate.kind !== "base-credit" ||
        !integer(candidate.amountCents) ||
        candidate.amountCents >= 0 ||
        "weeklyLimit" in candidate ||
        "weeklyLimitVersion" in candidate
      )
        return null;
      line = {
        lineId: candidate.groupId,
        lineClass: "customer_credit",
        kind: "base-credit",
        amountCents: candidate.amountCents,
      };
    } else {
      const parsed = parseWeeklyStatement([{ ...candidate, lineId: candidate.groupId }]);
      if (!parsed) return null;
      line = parsed.lines[0];
    }
    const group: WeeklyEconomicGroup = { ...line, groupId: candidate.groupId };
    if (candidate.originPeriod !== undefined || candidate.originInvoiceId !== undefined) {
      if (
        !text(candidate.originInvoiceId) ||
        !record(candidate.originPeriod) ||
        !isCivilDate(candidate.originPeriod.startsOn) ||
        !isCivilDate(candidate.originPeriod.endsOn) ||
        candidate.originPeriod.startsOn >= candidate.originPeriod.endsOn ||
        Date.parse(candidate.originPeriod.endsOn) - Date.parse(candidate.originPeriod.startsOn) >
          7 * 86400000
      )
        return null;
      group.originInvoiceId = candidate.originInvoiceId;
      group.originPeriod = {
        startsOn: candidate.originPeriod.startsOn,
        endsOn: candidate.originPeriod.endsOn,
      };
    }
    if (weeklyBilling && line.lineClass === "service" && !group.originPeriod) return null;
    if (candidate.metric !== undefined) {
      if (
        line.lineClass !== "service" ||
        typeof candidate.metric !== "string" ||
        !["view", "access"].includes(candidate.metric)
      )
        return null;
      group.metric = candidate.metric as "view" | "access";
    }
    if (candidate.occurrenceCount !== undefined) {
      if (!group.metric || !integer(candidate.occurrenceCount) || candidate.occurrenceCount <= 0)
        return null;
      group.occurrenceCount = candidate.occurrenceCount;
    }
    if (candidate.baseSourceCount !== undefined) {
      if (
        line.lineClass !== "service" ||
        !["base-hall", "base-radio", "base-tv"].includes(line.kind) ||
        !integer(candidate.baseSourceCount) ||
        candidate.baseSourceCount <= 0
      )
        return null;
      group.baseSourceCount = candidate.baseSourceCount;
    }
    if (candidate.serviceDates !== undefined) {
      if (
        line.lineClass !== "service" ||
        !Array.isArray(candidate.serviceDates) ||
        candidate.serviceDates.length > 7 ||
        !candidate.serviceDates.every(isCivilDate) ||
        new Set(candidate.serviceDates).size !== candidate.serviceDates.length
      )
        return null;
      group.serviceDates = [...candidate.serviceDates];
      if (
        group.originPeriod &&
        !group.serviceDates.every(
          (date) => date >= group.originPeriod!.startsOn && date < group.originPeriod!.endsOn,
        )
      )
        return null;
    }
    if (candidate.dailyAmounts !== undefined) {
      if (
        line.lineClass !== "service" ||
        !["base-hall", "base-radio", "base-tv"].includes(line.kind) ||
        !Array.isArray(candidate.dailyAmounts) ||
        candidate.dailyAmounts.length > 7 ||
        !group.serviceDates
      )
        return null;
      const dailyAmounts: NonNullable<WeeklyEconomicGroup["dailyAmounts"]> = [];
      let sum = 0;
      for (const daily of candidate.dailyAmounts) {
        if (
          !record(daily) ||
          !isCivilDate(daily.serviceDate) ||
          !integer(daily.amountCents) ||
          daily.amountCents < 0 ||
          (daily.sourceCount !== undefined &&
            (!integer(daily.sourceCount) || daily.sourceCount <= 0)) ||
          dailyAmounts.some((item) => item.serviceDate === daily.serviceDate)
        )
          return null;
        sum += daily.amountCents;
        if (!Number.isSafeInteger(sum)) return null;
        dailyAmounts.push({
          serviceDate: daily.serviceDate,
          amountCents: daily.amountCents,
          ...(daily.sourceCount !== undefined ? { sourceCount: daily.sourceCount } : {}),
        });
      }
      if (
        sum !== group.amountCents ||
        dailyAmounts.length !== group.serviceDates.length ||
        !dailyAmounts.every((item) => group.serviceDates?.includes(item.serviceDate))
      )
        return null;
      if (
        group.baseSourceCount !== undefined &&
        dailyAmounts.every((daily) => daily.sourceCount !== undefined)
      ) {
        const sourceCount = dailyAmounts.reduce(
          (total, daily) => total + (daily.sourceCount ?? 0),
          0,
        );
        if (!Number.isSafeInteger(sourceCount) || sourceCount !== group.baseSourceCount)
          return null;
      }
      group.dailyAmounts = dailyAmounts;
    }
    for (const field of ["quoteVersion", "economicCohortKey"] as const) {
      if (candidate[field] !== undefined) {
        if (line.lineClass !== "service" || !text(candidate[field])) return null;
        group[field] = candidate[field];
      }
    }
    for (const field of ["firstOccurredAt", "lastOccurredAt"] as const) {
      if (candidate[field] !== undefined) {
        if (line.lineClass !== "service" || !timestamp(candidate[field])) return null;
        group[field] = candidate[field];
      }
    }
    if (
      group.firstOccurredAt &&
      group.lastOccurredAt &&
      Date.parse(group.firstOccurredAt) > Date.parse(group.lastOccurredAt)
    )
      return null;
    groups.push(group);
  }
  return {
    invoiceId: value.invoiceId,
    invoiceStatus: value.invoiceStatus,
    summary,
    totalPages: value.totalPages,
    weeklyPeriod,
    weeklyCollection,
    weeklyBilling,
    groups,
  };
}
